import { ReactNode, useMemo } from 'react';
import ReactECharts from '../charts/echarts';
import type { CompareMode } from '../contract';
import { DirectionMeta, compareText, fmtPct, pctDirection } from '../format';
import styles from '../style.module.scss';

interface CompareProps {
  pct?: number | null;
  prevN?: number | null;
  /** Nguồn so sánh (đợt 1b). Không có + có prevN → "so n ngày đầu …" như đợt 1. */
  mode?: CompareMode | null;
  /** Câu nguồn của backend (`compare.label`); thiếu thì dùng câu mặc định theo chế độ. */
  source?: string | null;
  /** Ghi thêm sau % (vd. "trên 2.406 Creator"). */
  note?: string | null;
  /** Câu khi không có % (vd. `compareNote` của backend, "Không có số tháng trước"); '' = không hiện gì. */
  empty?: string | null;
}

interface Props extends CompareProps {
  label: string;
  value: string;
  /** Dòng phụ đã dựng sẵn (vd. "trên 120 Creator"); bỏ trống thì dựng từ pct / chế độ so. */
  sub?: ReactNode;
  /** Chuỗi theo ngày cho sparkline; rỗng → không vẽ. */
  series?: number[];
  color?: string;
}

/**
 * Dòng so tháng trước — màu + mũi tên + chữ, không chỉ màu. Ghi rõ nguồn: "so 5 ngày đầu tháng trước",
 * "Cả tháng so với tháng trước (theo file tháng TikTok)", "Dự kiến cả tháng so với tháng trước".
 */
export const CompareLine = ({
  pct,
  prevN,
  mode,
  source,
  note,
  empty,
  label = 'tháng trước',
}: CompareProps & { label?: string }) => {
  if (pct === null || pct === undefined) {
    if (empty === '') return null;
    return (
      <span className={styles.kpiSubMuted}>{empty ?? `Chưa đủ dữ liệu ${label} để so`}</span>
    );
  }
  const dir: DirectionMeta = pctDirection(pct);
  return (
    <span>
      {compareText(mode, source, prevN, label)}:{' '}
      <strong style={{ color: dir.color }}>
        {dir.arrow} {fmtPct(pct)}
      </strong>
      <span className={styles.srOnly}> ({dir.word})</span>
      {note ? <span className={styles.kpiNote}> · {note}</span> : null}
    </span>
  );
};

/** Thẻ KPI: số lớn + dòng so sánh + sparkline 36px. */
const KpiCard = ({
  label,
  value,
  sub,
  pct,
  prevN,
  mode,
  source,
  note,
  empty,
  series,
  color = '#C2337D',
}: Props) => {
  const option = useMemo(() => {
    if (!series || series.length < 2) return null;
    return {
      animation: false,
      grid: { left: 0, right: 0, top: 4, bottom: 2 },
      xAxis: { type: 'category', show: false, boundaryGap: false, data: series.map((_, i) => i) },
      yAxis: { type: 'value', show: false, min: 0 },
      tooltip: { show: false },
      series: [
        {
          type: 'line',
          data: series,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color },
          areaStyle: { color, opacity: 0.12 },
        },
      ],
    };
  }, [series, color]);

  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={styles.kpiValue}>{value}</span>
      <span className={styles.kpiSub}>
        {sub ?? (
          <CompareLine
            pct={pct}
            prevN={prevN}
            mode={mode}
            source={source}
            note={note}
            empty={empty}
          />
        )}
      </span>
      {option ? (
        <div className={styles.spark} aria-hidden>
          <ReactECharts option={option} style={{ height: 36, width: '100%' }} />
        </div>
      ) : (
        <div className={styles.sparkEmpty} aria-hidden />
      )}
    </div>
  );
};

export default KpiCard;
